import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { Zap } from 'lucide-react';
import { getActionSurgeProfile, applyFighterResourceUpdate } from '../../../shared/rules/fighter.mjs';
import apiFetch from '../../../utils/apiFetch';
import { notify } from '../../../utils/notification';
import SpellControlsBar from './SpellControlsBar';

export default function ActionSurgeControls({form,characterId,campaignId,combatState,isActiveTurn,onCharacterChange,onCombatStateReconciled}) {
  const apiId=String(form?._id || characterId);
  const storageKey=`fighter:action-surge:${campaignId || 'none'}:${apiId}`;
  const [pending,setPending]=useState(null),[busy,setBusy]=useState(false);
  const current=useRef(null),lock=useRef(false),scope=useRef(storageKey),live=useRef(true);
  scope.current=storageKey;
  const save=value=>{current.current=value;setPending(value);if(value)sessionStorage.setItem(storageKey,JSON.stringify(value));else sessionStorage.removeItem(storageKey);};
  useEffect(()=>{
    live.current=true;current.current=null;lock.current=false;setBusy(false);setPending(null);
    try {const value=JSON.parse(sessionStorage.getItem(storageKey)||'null');if(value?.request?.eventId){current.current=value;setPending(value);}} catch {sessionStorage.removeItem(storageKey);}
    return()=>{live.current=false;};
  },[storageKey]);
  const profile=getActionSurgeProfile(form);
  const inCombat=Number.isInteger(combatState?.activeTurn)&&Boolean(combatState?.participants?.[combatState.activeTurn]);
  const ledger=combatState?.turnActionLedger?.combatantId===String(characterId)&&combatState.turnActionLedger.turnSequence===combatState.turnSequence?combatState.turnActionLedger:null;
  const surgeFree=!ledger?.actionSurge||(ledger.actionSurge.haste&&ledger.actionSurge.spent&&!ledger.actionSurge.afterSurge);
  const available=profile.remaining>0&&inCombat&&isActiveTurn&&surgeFree&&!combatState?.pendingTurnAdvance;
  const use=async()=>{
    if(lock.current===storageKey)return;
    lock.current=storageKey;setBusy(true);
    const requestScope=storageKey;
    let selected=current.current || {request:{eventId:window.crypto?.randomUUID?.() || `action-surge:${Date.now()}:${Math.random()}`,turnSequence:inCombat?combatState.turnSequence:null}};
    try {
      save(selected);
      const response=await apiFetch(`/characters/${encodeURIComponent(apiId)}/fighter/action-surge`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(selected.request)});
      const result=await response.json();
      if(!response.ok)throw Object.assign(new Error(result.message || 'Action Surge could not be saved.'),{rejected:true});
      if(!live.current||scope.current!==requestScope)return;
      onCharacterChange?.(previous=>applyFighterResourceUpdate(previous,result.fighterResources));
      if(result.combat)onCombatStateReconciled?.(result.combat);
      if(!result.replayed)notify('Action Surge · One additional action is available this turn, excluding Magic.', 'success');
      save(null);
    }catch(error){
      if(live.current&&scope.current===requestScope)save({...selected,error:error.message,uncertain:!error.rejected});
    }finally{if(lock.current===requestScope)lock.current=false;if(live.current&&scope.current===requestScope)setBusy(false);}
  };
  useEffect(()=>{
    const activate=()=>{if(available&&!busy&&!pending)void use();};
    window.addEventListener('realmtracker:action-surge',activate);
    return()=>window.removeEventListener('realmtracker:action-surge',activate);
  });
  if(profile.level<2)return null;
  return <>
    <button type="button" data-action-tile="action-surge" className="combat-hud-resource-tile combat-hud-feature-action-tile" style={{'--hud-resource-accent':'#f3b35f'}}
      title="Action Surge · Additional action except Magic · Short or Long Rest recovery" aria-label={`Action Surge: ${profile.remaining} of ${profile.maximum} uses`}
      disabled={busy||(!pending&&!available)} onClick={use}>
      <span className="combat-hud-resource-tile__icon" aria-hidden="true"><Zap size={20}/></span>
      <span className="combat-hud-resource-tile__name">{busy?'Activating…':pending?'Retry Action Surge':'Action Surge'}</span>
      <span className="combat-hud-resource-tile__value">{profile.remaining}/{profile.maximum}</span>
    </button>
    {pending?.error&&<SpellControlsBar label="Action Surge" error={pending.error} hint={pending.uncertain?'Retry to confirm this activation.':'No new use was spent.'}
      actions={<><Button size="sm" disabled={busy} onClick={use}>Retry Action Surge</Button>{!pending.uncertain&&<Button size="sm" disabled={busy} onClick={()=>save(null)}>Cancel</Button>}</>}/>}
  </>;
}
